import React from "react";
import {
  ActivityIndicator,
  Pressable,
  PressableProps,
  StyleProp,
  StyleSheet,
  TextProps,
  View,
  ViewProps,
  ViewStyle,
} from "react-native";
import { PdText } from "src/components/layout";

import { tPressableState, tSvgProps } from "src/components/types";
import { kStyleConst } from "src/kStyle";
import {
  ButtonContext,
  tPdButtonColorScheme,
  tPdButtonSize,
  tPdButtonVariant,
  useButtonLabelStyles,
  useButtonStyles,
} from "./hooks";

export type tPdBaseButton = {
  isDisabled?: boolean;
  isLoading?: boolean;
  isFullWidth?: boolean;
  leftIcon?: React.ReactElement<tSvgProps>;
  rightIcon?: React.ReactElement<tSvgProps>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  pressed: boolean;
  hovered?: boolean;
  focused?: boolean;
} & ViewProps;

export type tPdButtonProps = {
  isDisabled?: boolean;
  isLoading?: boolean;
  isFullWidth?: boolean;
  leftIcon?: React.ReactElement<tSvgProps>;
  rightIcon?: React.ReactElement<tSvgProps>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  colorScheme?: tPdButtonColorScheme;
  size?: tPdButtonSize;
  variant?: tPdButtonVariant;
} & PressableProps &
  ViewProps;

const BaseButton: React.FC<tPdBaseButton> = ({
  isDisabled,
  isLoading,
  isFullWidth,
  leftIcon,
  rightIcon,
  pressed,
  hovered,
  focused,
  children,
  contentContainerStyle,
  ...rest
}) => {
  const { size } = React.useContext(ButtonContext);
  const buttonStyles = useButtonStyles(isDisabled || isLoading);
  const buttonLabelStyles = useButtonLabelStyles();

  let spinnerStyle = { transform: [{ scaleX: 1 }, { scaleY: 1 }] };
  if (size === "lg") spinnerStyle = { transform: [{ scaleX: 1.2 }, { scaleY: 1.2 }] };
  if (size === "sm") spinnerStyle = { transform: [{ scaleX: 0.7 }, { scaleY: 0.7 }] };

  return (
    <View style={[buttonStyles, contentContainerStyle]} {...rest}>
      {!!leftIcon && !isLoading && (
        <View style={styles.pdButtonLeftIcon}>
          {React.cloneElement(leftIcon, { color: buttonLabelStyles.color, style: leftIcon.props.style })}
        </View>
      )}
      {((!!leftIcon && !rightIcon) || (!leftIcon && !rightIcon)) && isLoading && (
        <View style={styles.pdButtonLeftIcon}>
          <ActivityIndicator style={spinnerStyle} color={buttonLabelStyles.color} />
        </View>
      )}
      {children}
      {!!rightIcon && !leftIcon && isLoading && (
        <View style={styles.pdButtonRightIcon}>
          <ActivityIndicator style={spinnerStyle} color={buttonLabelStyles.color} />
        </View>
      )}
      {!!rightIcon && !isLoading && (
        <View style={styles.pdButtonRightIcon}>
          {React.cloneElement(rightIcon, { color: buttonLabelStyles.color, style: rightIcon.props.style })}
        </View>
      )}
    </View>
  );
};

export const PdButton: React.FC<tPdButtonProps> = ({
  isFullWidth,
  isDisabled,
  isLoading,
  leftIcon,
  colorScheme = "green",
  size = "md",
  variant = "solid",
  onPress,
  style,
  ...rest
}) => {
  return (
    <Pressable disabled={isDisabled || isLoading} onPress={onPress} style={[isFullWidth ? styles.wFull : {}, style]}>
      {({ pressed, hovered, focused }: tPressableState) => (
        <ButtonContext.Provider value={{ colorScheme, size, variant, pressed, hovered, focused }}>
          <BaseButton {...{ isDisabled, isLoading, isFullWidth, leftIcon, pressed, hovered, focused }} {...rest} />
        </ButtonContext.Provider>
      )}
    </Pressable>
  );
};

export const PdButtonLabel: React.FC<TextProps> = ({ style, ...rest }) => {
  const buttonLabelStyles = useButtonLabelStyles();
  return <PdText style={[buttonLabelStyles, style]} {...rest} />;
};

const styles = StyleSheet.create({
  pdButtonLeftIcon: {
    alignItems: "center",
    justifyContent: "center",
    marginRight: 2 * kStyleConst.kSpacing,
  },
  pdButtonRightIcon: {
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 2 * kStyleConst.kSpacing,
  },
  wFull: {
    width: "100%",
  },
});
